import React, { useState, useEffect } from 'react';
import ScoreTracker from './ScoreTracker';
import RoleCard from './RoleCard';
import HistoryPanel from './HistoryPanel';

const ROLE_DESCRIPTIONS = {
  percival: { name: 'Percival', alignment: 'good', desc: 'Sees Merlin (and Morgana if in play)' },
  mordred: { name: 'Mordred', alignment: 'evil', desc: 'Hidden from Merlin\'s vision' },
  morgana: { name: 'Morgana', alignment: 'evil', desc: 'Appears as Merlin to Percival' },
  oberon: { name: 'Oberon', alignment: 'evil', desc: 'Unknown to other Evil players' },
};

function isGoodRole(role) {
  return ['Merlin', 'Percival', 'Loyal Servant of Arthur'].includes(role);
}

export default function ModeratorDashboard({
  emit, gameState, modState, playerId, roomCode, spectatorCode,
  roleInfo, voteResults, questResult, gameOverInfo,
  clearVoteResults, clearQuestResult, onNewGame, onLeave,
}) {
  const [error, setError] = useState('');
  const [modName, setModName] = useState('');
  const [selectedTeam, setSelectedTeam] = useState([]);
  const [lastPhase, setLastPhase] = useState(null);
  const [lastVoteResult, setLastVoteResult] = useState(null);
  const [delayMinutes, setDelayMinutes] = useState(0);
  const [delaySeconds, setDelaySeconds] = useState(0);
  const [connectionsExpanded, setConnectionsExpanded] = useState(false);

  const gs = gameState || {};
  const ms = modState || {};
  const phase = gs.phase || 'lobby';

  // Track phase changes to clear state
  useEffect(() => {
    if (phase !== lastPhase) {
      setLastPhase(phase);
      setError('');
      if (phase === 'team_building') {
        setSelectedTeam([]);
      }
    }
  }, [phase, lastPhase]);

  // Store last vote result for the outcome buttons
  useEffect(() => {
    if (voteResults) {
      setLastVoteResult(voteResults);
    }
  }, [voteResults]);

  // Sync delay inputs from server state
  useEffect(() => {
    const total = ms.spectatorDelay || 0;
    setDelayMinutes(Math.floor(total / 60));
    setDelaySeconds(total % 60);
  }, [ms.spectatorDelay]);

  const handleAction = async (event, data) => {
    setError('');
    const res = await emit(event, data);
    if (res?.error) {
      setError(res.error);
    }
    return res;
  };

  const handleSetDelay = (overrideSeconds) => {
    const totalSeconds = overrideSeconds !== undefined
      ? overrideSeconds
      : (parseInt(delayMinutes) || 0) * 60 + (parseInt(delaySeconds) || 0);
    handleAction('mod:setSpectatorDelay', { seconds: totalSeconds });
  };

  const handleClearDelay = () => {
    setDelayMinutes(0);
    setDelaySeconds(0);
    handleAction('mod:setSpectatorDelay', { seconds: 0 });
  };

  // ─── LOBBY ───
  if (phase === 'lobby') {
    return (
      <div className="mod-dashboard">
        <div className="room-code-display">
          <div className="label">Room Code</div>
          <div className="code">{gs.roomCode || roomCode}</div>
        </div>

        {/* Share Codes */}
        <div className="mod-section">
          <div className="mod-section-title">
            Share Codes
            {(gs.spectatorCount || 0) > 0 && (
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 8 }}>
                ({gs.spectatorCount} watching)
              </span>
            )}
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
              Player Room Code
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code className="spectator-link">{gs.roomCode || roomCode}</code>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => navigator.clipboard.writeText(gs.roomCode || roomCode)}
              >
                Copy
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
              Spectator Link
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code className="spectator-link">
                {window.location.origin}/spec/{spectatorCode || gs.spectatorCode}
              </code>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/spec/${spectatorCode || gs.spectatorCode}`
                  );
                }}
              >
                Copy
              </button>
            </div>
            <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: 4 }}>
              Share with stream viewers to show all roles and game info
            </div>
          </div>

          {/* Spectator Delay */}
          <SpectatorDelayControl
            delayMinutes={delayMinutes}
            delaySeconds={delaySeconds}
            currentDelay={ms.spectatorDelay || 0}
            onMinutesChange={setDelayMinutes}
            onSecondsChange={setDelaySeconds}
            onSet={handleSetDelay}
            onClearDelay={handleClearDelay}
          />
        </div>

        {/* Players list (reorderable) */}
        <div className="mod-section">
          <div className="mod-section-title">
            Players ({gs.playerCount || 0})
            {gs.playerCount >= 5 && gs.playerCount <= 10 && (
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 8 }}>
                {gs.teamCounts?.good}G / {gs.teamCounts?.evil}E
              </span>
            )}
          </div>
          {(gs.players || []).map((p, idx) => (
            <div key={p.id} className="mod-player-row">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="player-order-num">{idx + 1}.</span>
                <span className={`connected-dot ${p.connected ? '' : 'disconnected'}`} />
                {p.name}
                {p.id === playerId && gs.moderatorIsPlaying && (
                  <span style={{ color: 'var(--gold)', fontSize: '0.7rem', marginLeft: 6 }}>
                    (You)
                  </span>
                )}
                {!p.connected && (
                  <span style={{ color: 'var(--danger)', fontSize: '0.7rem', marginLeft: 6 }}>
                    (Disconnected)
                  </span>
                )}
                {gs.firstLeaderId === p.id && gs.manualFirstLeader && (
                  <span className="leader-badge" style={{ marginLeft: 6 }}>1st Leader</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button
                  className="btn btn-secondary btn-sm reorder-btn"
                  disabled={idx === 0}
                  onClick={() => {
                    const order = (gs.players || []).map(pl => pl.id);
                    [order[idx - 1], order[idx]] = [order[idx], order[idx - 1]];
                    handleAction('mod:reorderPlayers', { order });
                  }}
                  title="Move up"
                >
                  {'\u25B2'}
                </button>
                <button
                  className="btn btn-secondary btn-sm reorder-btn"
                  disabled={idx === (gs.players || []).length - 1}
                  onClick={() => {
                    const order = (gs.players || []).map(pl => pl.id);
                    [order[idx], order[idx + 1]] = [order[idx + 1], order[idx]];
                    handleAction('mod:reorderPlayers', { order });
                  }}
                  title="Move down"
                >
                  {'\u25BC'}
                </button>
                {p.id !== playerId && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleAction('mod:kickPlayer', { playerId: p.id })}
                  >
                    Kick
                  </button>
                )}
              </div>
            </div>
          ))}
          {(!gs.players || gs.players.length === 0) && (
            <div className="text-muted" style={{ fontSize: '0.85rem', padding: '8px 0' }}>
              No players yet. Share the room code!
            </div>
          )}
        </div>

        {/* First leader selection */}
        {(gs.playerCount || 0) >= 2 && (
          <div className="mod-section">
            <div className="mod-section-title">First Leader</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={gs.manualFirstLeader || false}
                  onChange={e => handleAction('mod:setLeaderMode', {
                    manual: e.target.checked,
                    leaderId: e.target.checked ? (gs.players?.[0]?.id || null) : null,
                  })}
                />
                <span className="toggle-slider" />
              </label>
              <span style={{ fontSize: '0.85rem' }}>
                {gs.manualFirstLeader ? 'Manual selection' : 'Random (default)'}
              </span>
            </div>
            {gs.manualFirstLeader && (
              <div className="team-selection">
                {(gs.players || []).map(p => (
                  <button
                    key={p.id}
                    className={`team-player-btn ${gs.firstLeaderId === p.id ? 'selected' : ''}`}
                    onClick={() => handleAction('mod:setLeaderMode', { manual: true, leaderId: p.id })}
                  >
                    <span>{p.name}</span>
                    <span className="select-indicator">
                      {gs.firstLeaderId === p.id ? '\u2713' : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Moderator playing toggle */}
        <div className="mod-section">
          <div className="mod-section-title">Moderator Participation</div>
          {gs.moderatorIsPlaying ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="info-box">
                You are playing as a participant. You'll be dealt a role.
              </div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleAction('mod:leaveAsPlayer')}
              >
                Stop Playing
              </button>
            </div>
          ) : (
            <div className="mod-play-toggle">
              <input
                type="text"
                placeholder="Your display name"
                value={modName}
                onChange={e => setModName(e.target.value)}
                maxLength={20}
              />
              <button
                className="btn btn-secondary btn-sm"
                disabled={!modName.trim()}
                onClick={() => handleAction('mod:joinAsPlayer', { playerName: modName.trim() })}
              >
                Join as Player
              </button>
            </div>
          )}
        </div>

        {/* Role configuration */}
        <div className="mod-section">
          <div className="mod-section-title">Optional Roles</div>
          <div className="role-config">
            <div className="role-toggle" style={{ opacity: 1 }}>
              <div>
                <div className="role-name good">Merlin</div>
                <div className="role-desc">Always included - knows Evil players</div>
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--success)' }}>Always On</span>
            </div>
            <div className="role-toggle" style={{ opacity: 1 }}>
              <div>
                <div className="role-name evil">Assassin</div>
                <div className="role-desc">Always included - can kill Merlin</div>
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--success)' }}>Always On</span>
            </div>
            {Object.entries(ROLE_DESCRIPTIONS).map(([key, r]) => (
              <div key={key} className="role-toggle">
                <div>
                  <div className={`role-name ${r.alignment}`}>{r.name}</div>
                  <div className="role-desc">{r.desc}</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={gs.optionalRoles?.[key] || false}
                    onChange={e => handleAction('mod:toggleRole', { role: key, enabled: e.target.checked })}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            ))}
          </div>
        </div>

        {error && <div className="error-msg">{error}</div>}

        {/* Start game button */}
        <button
          className="btn btn-gold btn-full"
          disabled={(gs.playerCount || 0) < 5}
          onClick={() => handleAction('mod:startGame')}
        >
          Start Game ({gs.playerCount || 0} players)
        </button>

        {(gs.playerCount || 0) < 5 && (
          <div className="text-muted text-center" style={{ fontSize: '0.8rem' }}>
            Need {5 - (gs.playerCount || 0)} more player{5 - (gs.playerCount || 0) !== 1 ? 's' : ''} to start
          </div>
        )}

        {/* Connections panel */}
        <ConnectionsPanel
          connections={ms.connections}
          ipAlerts={ms.ipAlerts}
          expanded={connectionsExpanded}
          onToggle={() => setConnectionsExpanded(e => !e)}
        />

        <button
          className="btn btn-secondary btn-full mt-16"
          onClick={() => {
            if (window.confirm('Leave this game? You can rejoin later as moderator.')) {
              onLeave();
            }
          }}
        >
          Leave Game
        </button>
      </div>
    );
  }

  // ─── IN-GAME (moderator view) ───
  const leader = gs.players?.find(p => p.id === gs.leaderId);
  const teamNames = (gs.proposedTeam || []).map(
    tid => gs.players?.find(p => p.id === tid)?.name || '?'
  );

  const renderPhaseControls = () => {
    switch (phase) {
      case 'role_reveal':
        return (
          <div className="mod-section">
            <div className="mod-section-title">Phase: Role Reveal</div>
            <div className="text-muted mb-8" style={{ fontSize: '0.85rem' }}>
              Players are viewing their secret roles.
            </div>
            {/* Show moderator's own role if playing */}
            {gs.moderatorIsPlaying && roleInfo && (
              <div className="mb-16">
                <RoleCard roleInfo={roleInfo} compact={false} />
              </div>
            )}
            <button
              className="btn btn-gold btn-full"
              onClick={() => handleAction('mod:advanceToTeamBuilding')}
            >
              Begin Quest 1 - Team Building
            </button>
          </div>
        );

      case 'team_building':
        return (
          <div className="mod-section">
            <div className="mod-section-title">
              Phase: Team Building (Quest {gs.currentQuest + 1})
            </div>
            <div className="mb-8" style={{ fontSize: '0.85rem' }}>
              <strong className="text-gold">{leader?.name}</strong> is the Leader.
              Select <strong>{gs.teamSize}</strong> team members.
            </div>
            <div className="team-selection">
              {(gs.players || []).map(p => {
                const isSelected = selectedTeam.includes(p.id);
                return (
                  <button
                    key={p.id}
                    className={`team-player-btn ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedTeam(prev => prev.filter(id => id !== p.id));
                      } else if (selectedTeam.length < gs.teamSize) {
                        setSelectedTeam(prev => [...prev, p.id]);
                      }
                    }}
                  >
                    <span>
                      {p.name}
                      {p.id === gs.leaderId && <span className="leader-badge"> - Leader</span>}
                    </span>
                    <span className="select-indicator">
                      {isSelected ? '\u2713' : ''}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="text-muted text-center mt-8" style={{ fontSize: '0.8rem' }}>
              {selectedTeam.length} / {gs.teamSize} selected
            </div>
            <div className="mod-actions mt-8">
              <button
                className="btn btn-gold btn-full"
                disabled={selectedTeam.length !== gs.teamSize}
                onClick={async () => {
                  const res = await handleAction('mod:proposeTeam', { team: selectedTeam });
                  if (res?.success) {
                    await handleAction('mod:startVoting');
                  }
                }}
              >
                Propose Team & Start Vote
              </button>
            </div>
            <RejectionTracker count={gs.consecutiveRejections} />
          </div>
        );

      case 'voting':
        return (
          <div className="mod-section">
            <div className="mod-section-title">Phase: Voting</div>
            <div className="mb-8" style={{ fontSize: '0.85rem' }}>
              Team: <strong>{teamNames.join(', ')}</strong>
            </div>
            <div className="mb-8" style={{ fontSize: '0.85rem' }}>
              Votes: <strong>{gs.votesSubmitted} / {gs.playerCount}</strong>
            </div>
            {/* Show moderator secret vote info (only in Game Master mode) */}
            {!gs.moderatorIsPlaying && ms.allVotes && Object.keys(ms.allVotes).length > 0 && (
              <div className="mb-8">
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                  Votes received (hidden from players):
                </div>
                {Object.entries(ms.allVotes).map(([pid, { name, vote }]) => (
                  <div key={pid} className="vote-result-item">
                    <span>{name}</span>
                    <span className={`vote-badge ${vote}`}>
                      {vote === 'approve' ? 'Approve' : 'Reject'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {/* Show moderator vote panel if playing */}
            {gs.moderatorIsPlaying && <ModeratorVotePanel emit={emit} gameState={gs} playerId={playerId} />}
            <button
              className="btn btn-gold btn-full mt-8"
              disabled={gs.votesSubmitted < gs.playerCount}
              onClick={() => handleAction('mod:revealVotes')}
            >
              {gs.votesSubmitted < gs.playerCount
                ? `Waiting for votes (${gs.votesSubmitted}/${gs.playerCount})`
                : 'Reveal Votes'}
            </button>
          </div>
        );

      case 'vote_result':
        return (
          <div className="mod-section">
            <div className="mod-section-title">Phase: Vote Results</div>
            {lastVoteResult && (
              <>
                <div className="card-header" style={{
                  color: lastVoteResult.approved ? 'var(--success)' : 'var(--danger)',
                  fontSize: '1rem',
                  marginBottom: 8,
                }}>
                  Team {lastVoteResult.approved ? 'Approved' : 'Rejected'} ({lastVoteResult.approves}-{lastVoteResult.rejects})
                </div>
                <div className="vote-results mb-8">
                  {Object.entries(lastVoteResult.voteDetails).map(([pid, { name, vote }]) => (
                    <div key={pid} className="vote-result-item">
                      <span>{name}</span>
                      <span className={`vote-badge ${vote}`}>
                        {vote === 'approve' ? 'Approve' : 'Reject'}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
            <button
              className="btn btn-gold btn-full"
              onClick={() => handleAction('mod:handleVoteOutcome', {
                approved: lastVoteResult?.approved ?? false,
              })}
            >
              {lastVoteResult?.approved ? 'Proceed to Quest' : 'Next Team (Leader Advances)'}
            </button>
          </div>
        );

      case 'quest':
        return (
          <div className="mod-section">
            <div className="mod-section-title">Phase: Quest {gs.currentQuest + 1}</div>
            <div className="mb-8" style={{ fontSize: '0.85rem' }}>
              Team: <strong>{teamNames.join(', ')}</strong>
            </div>
            <div className="mb-8" style={{ fontSize: '0.85rem' }}>
              Cards: <strong>{gs.questCardsSubmitted} / {gs.proposedTeam?.length}</strong>
              {gs.failsRequired > 1 && (
                <span className="warning-box" style={{ display: 'inline', marginLeft: 8, padding: '2px 8px' }}>
                  Needs {gs.failsRequired} fails to fail
                </span>
              )}
            </div>
            {/* Show moderator secret quest card info (only in Game Master mode) */}
            {!gs.moderatorIsPlaying && ms.allQuestCards && Object.keys(ms.allQuestCards).length > 0 && (
              <div className="mb-8">
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                  Cards played (hidden from players):
                </div>
                {Object.entries(ms.allQuestCards).map(([pid, { name, card }]) => (
                  <div key={pid} className="vote-result-item">
                    <span>{name}</span>
                    <span className={`vote-badge ${card === 'success' ? 'approve' : 'reject'}`}>
                      {card === 'success' ? 'Success' : 'Fail'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {/* Moderator quest card if playing and on team */}
            {gs.moderatorIsPlaying && gs.proposedTeam?.includes(playerId) && (
              <ModeratorQuestPanel emit={emit} gameState={gs} playerId={playerId} roleInfo={roleInfo} />
            )}
            <button
              className="btn btn-gold btn-full mt-8"
              disabled={gs.questCardsSubmitted < (gs.proposedTeam?.length || 0)}
              onClick={() => handleAction('mod:revealQuestResult')}
            >
              {gs.questCardsSubmitted < (gs.proposedTeam?.length || 0)
                ? `Waiting for cards (${gs.questCardsSubmitted}/${gs.proposedTeam?.length})`
                : 'Reveal Quest Result'}
            </button>
          </div>
        );

      case 'quest_result':
        return (
          <div className="mod-section">
            <div className="mod-section-title">Phase: Quest Result</div>
            {questResult && (
              <div className="quest-result-display">
                <div className="result-icon">
                  {questResult.questSuccess ? '\u2694' : '\u2620'}
                </div>
                <div className={`result-text ${questResult.questSuccess ? 'success' : 'fail'}`}>
                  Quest {questResult.questSuccess ? 'Succeeded!' : 'Failed!'}
                </div>
                <div className="result-cards">
                  {questResult.successCount} Success / {questResult.failCount} Fail
                </div>
                <div className="text-muted mt-8">
                  Good: {questResult.goodWins} | Evil: {questResult.evilWins}
                </div>
              </div>
            )}
            <button
              className="btn btn-gold btn-full mt-8"
              onClick={() => handleAction('mod:advanceAfterQuest')}
            >
              Continue
            </button>
          </div>
        );

      case 'assassination':
        return (
          <AssassinationPanel
            emit={emit}
            gameState={gs}
            modState={ms}
            handleAction={handleAction}
          />
        );

      case 'game_over':
        return (
          <div className="mod-section">
            <div className="game-over">
              <div className={`winner-text ${gameOverInfo?.winner || gs.winner}`}>
                {(gameOverInfo?.winner || gs.winner) === 'good' ? 'Good Wins!' : 'Evil Wins!'}
              </div>
              <div className="win-reason">
                {gameOverInfo?.reason || gs.winReason}
              </div>
            </div>
            {/* Show all roles */}
            {ms.allRoles && (
              <div className="mt-16">
                <div className="mod-section-title">Role Reveal</div>
                {ms.allRoles.map(p => (
                  <div key={p.id} className="mod-player-row">
                    <span>{p.name}</span>
                    <span className={`role-badge ${
                      ['Merlin', 'Percival', 'Loyal Servant of Arthur'].includes(p.role)
                        ? 'good-role' : 'evil-role'
                    }`}>
                      {p.role}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="mod-actions mt-16">
              <button
                className="btn btn-gold btn-full"
                onClick={async () => {
                  await handleAction('mod:newGame');
                  onNewGame();
                }}
              >
                New Game (Same Room)
              </button>
              <button
                className="btn btn-secondary btn-full"
                onClick={onLeave}
              >
                Leave
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mod-dashboard">
      <ScoreTracker gameState={gs} />

      {/* Player/Role reference during game */}
      {phase !== 'lobby' && phase !== 'game_over' && ms.allRoles && (
        gs.moderatorIsPlaying ? (
          <>
            {/* Player-Moderator mode: show own role + player list without roles */}
            {roleInfo && (
              <div className="mod-section">
                <div className="mod-section-title">Your Role</div>
                <RoleCard roleInfo={roleInfo} compact={true} />
              </div>
            )}
            <div className="mod-section">
              <div className="mod-section-title">Players</div>
              {ms.allRoles.map(p => (
                <div key={p.id} className="mod-player-row">
                  <div>
                    <span className={`connected-dot ${p.connected ? '' : 'disconnected'}`} />
                    {p.name}
                    {p.id === playerId && (
                      <span style={{ color: 'var(--gold)', fontSize: '0.7rem', marginLeft: 6 }}>(You)</span>
                    )}
                    {!p.connected && (
                      <span style={{ color: 'var(--danger)', fontSize: '0.7rem', marginLeft: 6 }}>(Disconnected)</span>
                    )}
                    {p.id === gs.leaderId && <span className="leader-badge"> - Leader</span>}
                    {gs.proposedTeam?.includes(p.id) && <span className="on-team"> - On Team</span>}
                  </div>
                  {!p.connected && p.id !== playerId && phase !== 'game_over' && (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => {
                        if (window.confirm(
                          `Kick ${p.name}? They are disconnected. This may affect game balance.`
                        )) {
                          handleAction('mod:kickPlayer', { playerId: p.id, force: true });
                        }
                      }}
                    >
                      Kick
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Game Master mode: show all roles */
          <div className="mod-section">
            <div className="mod-section-title">
              Players & Roles (hidden from players)
            </div>
            {ms.allRoles.map(p => (
              <div key={p.id} className="mod-player-row">
                <div>
                  <span className={`connected-dot ${p.connected ? '' : 'disconnected'}`} />
                  {p.name}
                  {!p.connected && (
                    <span style={{ color: 'var(--danger)', fontSize: '0.7rem', marginLeft: 6 }}>(Disconnected)</span>
                  )}
                  {p.id === gs.leaderId && <span className="leader-badge"> - Leader</span>}
                  {gs.proposedTeam?.includes(p.id) && <span className="on-team"> - On Team</span>}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span className={`role-badge ${isGoodRole(p.role) ? 'good-role' : 'evil-role'}`}>
                    {p.role}
                  </span>
                  {!p.connected && phase !== 'game_over' && (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => {
                        if (window.confirm(
                          `Kick ${p.name}? They are disconnected and have the role "${p.role}" (${isGoodRole(p.role) ? 'Good' : 'Evil'}). This will affect game balance.`
                        )) {
                          handleAction('mod:kickPlayer', { playerId: p.id, force: true });
                        }
                      }}
                    >
                      Kick
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {error && <div className="error-msg">{error}</div>}

      {renderPhaseControls()}

      {/* History panel (visible for Game Master mode) */}
      {phase !== 'lobby' && !gs.moderatorIsPlaying && (
        <HistoryPanel gameState={gs} />
      )}

      {/* End game button (always available during active game) */}
      {phase !== 'lobby' && phase !== 'game_over' && (
        <>
          {/* Spectator delay control (accessible mid-game) */}
          <div className="mod-section">
            <SpectatorDelayControl
              delayMinutes={delayMinutes}
              delaySeconds={delaySeconds}
              currentDelay={ms.spectatorDelay || 0}
              onMinutesChange={setDelayMinutes}
              onSecondsChange={setDelaySeconds}
              onSet={handleSetDelay}
            />
          </div>

          <button
            className="btn btn-danger btn-full mt-16"
            onClick={() => {
              if (window.confirm('End the game early?')) {
                handleAction('mod:endGame', { winner: 'none', reason: 'Game ended by moderator' });
              }
            }}
          >
            End Game Early
          </button>
          <button
            className="btn btn-secondary btn-full mt-8"
            onClick={() => {
              if (window.confirm('Leave the game? The game will continue. You can rejoin later.')) {
                onLeave();
              }
            }}
          >
            Leave Game
          </button>
        </>
      )}

      {/* Connections panel (always at bottom, collapsed by default) */}
      {phase !== 'lobby' && (
        <ConnectionsPanel
          connections={ms.connections}
          ipAlerts={ms.ipAlerts}
          expanded={connectionsExpanded}
          onToggle={() => setConnectionsExpanded(e => !e)}
        />
      )}
    </div>
  );
}

// ─── Spectator Delay Control ───

function SpectatorDelayControl({ delayMinutes, delaySeconds, currentDelay, onMinutesChange, onSecondsChange, onSet, onClearDelay }) {
  const isActive = currentDelay > 0;
  const currentMins = Math.floor(currentDelay / 60);
  const currentSecs = currentDelay % 60;

  return (
    <div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 6 }}>
        Spectator Delay
        {isActive && (
          <span style={{ marginLeft: 8, color: 'var(--warning)', fontWeight: 600 }}>
            Active: {currentMins > 0 ? `${currentMins}m ` : ''}{currentSecs}s
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="number"
          min="0"
          max="59"
          value={delayMinutes}
          onChange={e => onMinutesChange(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
          style={{
            width: 52,
            padding: '8px',
            background: 'var(--bg-input)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-primary)',
            fontSize: '0.9rem',
            fontFamily: 'inherit',
            textAlign: 'center',
          }}
        />
        <span style={{ color: 'var(--text-muted)', fontWeight: 700 }}>m</span>
        <input
          type="number"
          min="0"
          max="59"
          value={delaySeconds}
          onChange={e => onSecondsChange(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
          style={{
            width: 52,
            padding: '8px',
            background: 'var(--bg-input)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-primary)',
            fontSize: '0.9rem',
            fontFamily: 'inherit',
            textAlign: 'center',
          }}
        />
        <span style={{ color: 'var(--text-muted)', fontWeight: 700 }}>s</span>
        <button
          className="btn btn-secondary btn-sm"
          onClick={onSet}
          style={{ marginLeft: 4 }}
        >
          Set
        </button>
        {isActive && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClearDelay}
            style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}
          >
            Clear
          </button>
        )}
      </div>
      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 4 }}>
        {isActive ? 'Spectators see game info with a delay (anti-cheat)' : 'Real-time (no delay)'}
      </div>
    </div>
  );
}

// ─── Connections Panel ───

function ConnectionsPanel({ connections, ipAlerts, expanded, onToggle }) {
  const count = connections?.length || 0;
  const alertCount = ipAlerts?.length || 0;

  return (
    <div className="mod-section" style={{ border: alertCount > 0 ? '1px solid rgba(239,68,68,0.3)' : undefined }}>
      <div
        className="mod-section-title"
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: expanded ? 10 : 0 }}
        onClick={onToggle}
      >
        <span>
          Connections ({count})
          {alertCount > 0 && (
            <span style={{ marginLeft: 8, color: 'var(--danger)', fontSize: '0.75rem' }}>
              ⚠ {alertCount} IP alert{alertCount !== 1 ? 's' : ''}
            </span>
          )}
        </span>
        <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <>
          {alertCount > 0 && (
            <div style={{ marginBottom: 8 }}>
              {ipAlerts.map((alert, i) => (
                <div key={i} className="ip-alert">
                  ⚠️ {alert.message}
                </div>
              ))}
            </div>
          )}

          {count === 0 ? (
            <div className="text-muted" style={{ fontSize: '0.8rem' }}>No connections logged.</div>
          ) : (
            (connections || []).map(conn => {
              const isFlagged = ipAlerts?.some(
                a => a.playerSocketId === conn.socketId || a.spectatorSocketId === conn.socketId
              );
              return (
                <div key={conn.socketId} className={`connection-row ${isFlagged ? 'ip-flagged' : ''}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span className={`conn-type-badge conn-type-${conn.type}`}>{conn.type}</span>
                    {conn.playerName && <strong style={{ fontSize: '0.85rem' }}>{conn.playerName}</strong>}
                    {isFlagged && <span style={{ color: 'var(--danger)', fontSize: '0.75rem' }}>⚠</span>}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 1 }}>
                    IP: {conn.ip}
                  </div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginBottom: 1 }}>
                    {conn.platform !== 'unknown' ? `${conn.platform} · ` : ''}
                    {conn.screenResolution !== 'unknown' ? `${conn.screenResolution} · ` : ''}
                    {new Date(conn.connectedAt).toLocaleTimeString()}
                  </div>
                  <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                    {conn.userAgent?.substring(0, 80)}{conn.userAgent?.length > 80 ? '…' : ''}
                  </div>
                </div>
              );
            })
          )}
        </>
      )}
    </div>
  );
}

// ─── Sub-components used only in moderator view ───

function ModeratorVotePanel({ emit, gameState, playerId }) {
  const [voted, setVoted] = useState(false);

  useEffect(() => {
    setVoted(false);
  }, [gameState.phase]);

  if (voted || gameState.phase !== 'voting') return null;

  const handleVote = async (vote) => {
    const res = await emit('player:vote', { vote });
    if (res?.success) setVoted(true);
  };

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8, marginTop: 8 }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
        Your vote (as player):
      </div>
      <div className="vote-buttons">
        <button className="btn btn-approve btn-sm" onClick={() => handleVote('approve')}>
          Approve
        </button>
        <button className="btn btn-reject btn-sm" onClick={() => handleVote('reject')}>
          Reject
        </button>
      </div>
    </div>
  );
}

function ModeratorQuestPanel({ emit, gameState, playerId, roleInfo }) {
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    setPlayed(false);
  }, [gameState.phase]);

  if (played || gameState.phase !== 'quest') return null;

  const isEvil = roleInfo?.alignment === 'evil';

  const handlePlay = async (card) => {
    const res = await emit('player:questCard', { card });
    if (res?.success) setPlayed(true);
  };

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8, marginTop: 8 }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
        Your quest card (as player):
      </div>
      <div className="quest-buttons">
        <button className="btn btn-success-card btn-sm" onClick={() => handlePlay('success')}>
          Success
        </button>
        {isEvil && (
          <button className="btn btn-fail-card btn-sm" onClick={() => handlePlay('fail')}>
            Fail
          </button>
        )}
      </div>
    </div>
  );
}

function AssassinationPanel({ emit, gameState, modState, handleAction }) {
  const [targetId, setTargetId] = useState(null);
  const isPlaying = gameState.moderatorIsPlaying;

  // When playing: show all players (moderator doesn't know roles)
  // When not playing: show only good players (moderator knows roles)
  const targetablePlayers = isPlaying
    ? (gameState.players || [])
    : (modState.allRoles || []).filter(p =>
        ['Merlin', 'Percival', 'Loyal Servant of Arthur'].includes(p.role)
      );

  return (
    <div className="mod-section">
      <div className="mod-section-title" style={{ color: 'var(--evil-light)' }}>
        Assassination Phase
      </div>
      <div className="mb-8" style={{ fontSize: '0.85rem' }}>
        Good completed 3 quests! The Assassin gets one chance to identify Merlin.
        Discuss verbally on Zoom, then select who the Assassin targets:
      </div>
      <div className="team-selection">
        {targetablePlayers.map(p => (
          <button
            key={p.id}
            className={`team-player-btn ${targetId === p.id ? 'selected' : ''}`}
            onClick={() => setTargetId(p.id)}
          >
            <span>{p.name}</span>
            <span className="select-indicator">
              {targetId === p.id ? '\u2713' : ''}
            </span>
          </button>
        ))}
      </div>
      <div className="assassination-buttons mt-8">
        <button
          className="btn btn-evil btn-full"
          disabled={!targetId}
          onClick={() => {
            handleAction('mod:resolveAssassination', { targetId });
          }}
        >
          Confirm Assassination
        </button>
      </div>
      {targetId && (
        <div className="text-muted text-center mt-8" style={{ fontSize: '0.8rem' }}>
          Target: {(targetablePlayers.find(p => p.id === targetId))?.name}
          {!isPlaying && modState.allRoles && (() => {
            const target = modState.allRoles.find(p => p.id === targetId);
            return target?.role ? (
              <span style={{ color: 'var(--text-muted)' }}> ({target.role})</span>
            ) : null;
          })()}
        </div>
      )}
    </div>
  );
}

function RejectionTracker({ count }) {
  return (
    <div style={{ textAlign: 'center', marginTop: '12px' }}>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
        Vote Track
      </div>
      <div className="rejection-tracker">
        {[0, 1, 2, 3, 4].map(i => (
          <div
            key={i}
            className={`rejection-pip ${
              i < count ? (i >= 3 ? 'danger' : 'filled') : ''
            }`}
          />
        ))}
      </div>
      {count >= 4 && (
        <div className="warning-box mt-8">
          One more rejection and Evil wins!
        </div>
      )}
    </div>
  );
}
